<script lang="ts">
import { getRandomNumber } from '@/utils/utils'
import {
  useDebounceFn,
  useMutationObserver,
  useResizeObserver,
  useScroll,
} from '@vueuse/core'
import { defineComponent, useTemplateRef, watch } from 'vue'

export type ScrollboxScrollOptions = {
  /**
   * Determines where the selected item will end up when it scrolls into view.
   * `start` will snap the left edge of the item to the left side of the
   * container, and `end` will snap the right edge of the item to the right
   * side of the container. Note: CSS scroll-snapping may result in `end` not
   * aligning to the right-hand edge because the left-most item will be the
   * snapping context.
   */
  align: 'start' | 'end'

  /**
   * Determines if the item to scroll to should be marked as selected. If `true`,
   * the `currentIndex` will be updated to the index of the selected item.
   * Defaults to `true`.
   */
  select?: boolean
}

export interface ScrollboxScrollEvent {
  direction: 'toStart' | 'toEnd'
}

export type VisibleIndexesOptions = {
  /**
   * Should partially visible items be included in the results. Defaults to
   * `false`
   */
  includePartial?: boolean
  /**
   * Grow or shrink the scrollbox bounding box before computing which items are
   * visible. Positive values will shrink the bounding box and negative values
   * will expand it. Defaults to `0`.
   */
  margin?: number
}

export type ScrollboxInitializedEvent = {
  scrollbox: InstanceType<typeof CarouselScrollBox>
  wrapper: HTMLDivElement
  children: HTMLElement[]
}

const CarouselScrollBox = defineComponent({
  props: {
    /**
     * The initial index to scroll to when the component is mounted.
     * Note this is different to the visible page which may show
     * multiple cards.
     *
     * @type {number}
     * @default 0
     */
    initialIndex: {
      type: Number,
      default: 0,
    },

    /**
     * Determines if the scrollbox should autoplay. If `true`, the
     * scrollbox will automatically scroll to the next item
     * every 4 seconds.
     */
    autoplay: {
      type: Boolean,
      default: false,
    },

    /**
     * The interval in milliseconds between each autoplay scroll.
     * Defaults to `4000` (4 seconds).
     */
    interval: {
      type: Number,
      default: 4000,
    },

    /**
     * The delay in milliseconds before autoplay starts.
     * Defaults to `0` (no delay).
     */
    autoplayDelay: {
      type: Number,
      default: 0,
    },
  },

  emits: ['initialized', 'scrollstart', 'scrollstop'],

  setup() {
    const wrapper = useTemplateRef<HTMLDivElement>('wrapper')
    const { x, isScrolling, arrivedState, measure } = useScroll(wrapper, {
      behavior: 'smooth',
    })

    const { stop: stopResizeObserver } = useResizeObserver(wrapper, measure)

    return {
      arrivedState,
      scrollX: x,
      stopResizeObserver,
      wrapper,
      isScrolling,
    }
  },
  data() {
    return {
      autoplayInterval: null as ReturnType<typeof setInterval> | null,
      autoplayTimeout: null as ReturnType<typeof setTimeout> | null,
      currentIndex: 0,
    }
  },
  computed: {
    /**
     * Determines if the scrollbox is at the start of its travel and therefore
     * cannot scroll left */
    atStart(): boolean {
      return this.arrivedState.left
    },
    /**
     * Determines if the scrollbox is at the end of its travel and therefore
     * cannot scroll right
     */
    atEnd(): boolean {
      return this.arrivedState.right
    },
  },
  mounted() {
    watch(
      () => this.isScrolling,
      (value) => {
        if (value === false) {
          this.currentIndex =
            this.getVisibleIndexes().at(0) ?? this.currentIndex

          this.$emit('scrollstop', {
            currentIndex: this.currentIndex,
          })
        } else {
          this.$emit('scrollstart', {
            currentIndex: this.currentIndex,
          })
        }
      }
    )

    void this.$nextTick(() => {
      this.scrollTo(this.initialIndex, {
        align: 'start',
      })

      if (this.autoplay) {
        this.autoplayTimeout = setTimeout(() => {
          this.startAutoplay()
        }, this.autoplayDelay)
      }

      void this.$nextTick(() => {
        this.emitInit()
      })

      // observer on children - timeout mightn't catch all (delayed) inserted children
      useMutationObserver(
        this.wrapper,
        useDebounceFn(() => {
          this.emitInit()
        }, 500),
        {
          childList: true,
          subtree: true,
        }
      )
    })
  },
  beforeUnmounted() {
    if (this.autoplayTimeout) {
      clearTimeout(this.autoplayTimeout)
      this.autoplayTimeout = null
    }
    this.stopAutoplay()
    this.stopResizeObserver()
  },
  methods: {
    /**
     * Starts the autoplay feature which will automatically
     * scroll to the next item every `interval` milliseconds.
     */
    startAutoplay() {
      if (this.autoplayInterval) {
        return
      }

      this.autoplayInterval = setInterval(() => {
        if (!this.atEnd) {
          this.step()
        } else {
          this.scrollTo(0, {
            align: 'start',
          })
        }
      }, this.interval)
    },
    /**
     * Stops the autoplay feature if it is currently running.
     */
    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval)
        this.autoplayInterval = null
      }
    },
    /**
     * Scrolls the child element of the specified index into position so that
     * it aligns to the left side of the scrollbox. Returns `false` if the
     * selected element does not exist, otherwise `true`.
     *
     * @param index The index of the child element to scroll into view
     * @param options The scrolling options
     */
    scrollTo(
      index: number,
      options: ScrollboxScrollOptions = {
        align: 'start',
        select: true,
      }
    ): boolean {
      const { align, select } = options

      const wrapper = this.wrapper

      if (!wrapper) {
        return false
      }

      const count = wrapper.children.length

      if (index < 0 || index > count - 1) {
        return false
      }

      const target = wrapper.children[index]

      if (!target) {
        return false
      }

      const wrapperBounds = wrapper.getBoundingClientRect()
      const targetBounds = target.getBoundingClientRect()
      const delta = targetBounds.left - wrapperBounds.left
      let left = this.scrollX

      if (align === 'start') {
        left += delta
      } else if (align === 'end') {
        left += delta - wrapperBounds.width + targetBounds.width
      }

      this.scrollX = Math.floor(left)

      if (select || select === undefined) {
        this.currentIndex = index
      }

      return true
    },

    /**
     * Scrolls to the first child element so that it aligns to the left side of the scrollbox.
     * Returns `false` if there are no child elements, otherwise `true`.
     */
    scrollToStart() {
      return this.scrollTo(0, {
        align: 'start',
      })
    },

    /**
     * Scrolls to the last child element so that it aligns to the right side of the scrollbox.
     * Returns `false` if there are no child elements, otherwise `true`.
     */
    scrollToEnd() {
      const wrapper = this.wrapper

      if (!wrapper) {
        return false
      }

      const count = wrapper.children.length

      return this.scrollTo(count - 1, {
        align: 'end',
      })
    },

    /**
     * Returns an array of children starting at the start index.
     *
     * @param startIndex The starting index of the child to return.
     * @param endIndex The ending index of the child to return.
     */
    getChildrenAt(startIndex: number, endIndex?: number): Array<HTMLElement> {
      const wrapper = this.wrapper

      if (!wrapper) {
        return []
      }

      return Array.from(wrapper.children)
        .splice(startIndex, endIndex ?? startIndex + 1)
        .map((child: Element) => child.children[0] as HTMLElement)
        .filter((e): e is HTMLElement => !!e)
    },

    /**
     * Returns an array of indexes representing the elements that are visible
     * inside the scroll box.
     *
     * If an element is only partially visible, it will not be included in the
     * result. You can change this behaviour by setting `includePartial` option.
     *
     * You can also adjust the bounding box use to determine if an element is
     * visible using the `margin` option.
     */
    getVisibleIndexes(options: VisibleIndexesOptions = {}): number[] {
      const indexes: number[] = []
      const wrapper = this.wrapper
      const { margin = 0, includePartial = false } = options

      if (wrapper) {
        const wrapperBounds = wrapper.getBoundingClientRect()

        const leftEdge = wrapperBounds.left + margin - 1
        const rightEdge = wrapperBounds.right - margin + 1

        if (includePartial) {
          for (let c = 0; c < wrapper.children.length; c++) {
            const childBounds = wrapper.children[c].getBoundingClientRect()
            if (childBounds.right > leftEdge && childBounds.left < rightEdge) {
              indexes.push(c)
            }
          }
        } else {
          for (let c = 0; c < wrapper.children.length; c++) {
            const childBounds = wrapper.children[c].getBoundingClientRect()
            if (
              childBounds.left >= leftEdge &&
              childBounds.right <= rightEdge
            ) {
              indexes.push(c)
            }
          }
        }
      }
      return indexes
    },

    /**
     * Steps forward one page
     */
    nextPage() {
      const indexes = this.getVisibleIndexes()
      const lastVisibleIndex = indexes.at(-1)

      if (lastVisibleIndex !== undefined) {
        this.scrollTo(lastVisibleIndex + 1, {
          align: 'start',
        })
      }
    },

    /**
     * Steps backwards by one page
     */
    previousPage() {
      const indexes = this.getVisibleIndexes()
      const previousVisibleIndex = indexes.at(0)

      if (previousVisibleIndex !== undefined) {
        this.scrollTo(previousVisibleIndex - 1, {
          align: 'end',
        })
      }
    },

    /**
     * Steps forward or backward by the given number of child elements. If
     * `step` is positive, the scrollbox will move forward. If `step` is
     * negative the scrollbox will move backward
     */
    step(step = 1) {
      const indexes = this.getVisibleIndexes()
      const firstVisibleIndex = indexes.at(0)

      if (firstVisibleIndex !== undefined) {
        this.scrollTo(firstVisibleIndex + step, {
          align: 'start',
        })
      }
    },

    /**
     * Emits the `initialized` event with the scrollbox instance, the wrapper
     * element, and the children of the wrapper.
     */
    emitInit() {
      if (!this.wrapper) {
        return
      }

      const children = Array.from(this.wrapper?.children ?? [])
        .map((child: Element) => child.children[0] as HTMLElement)
        .filter((e): e is HTMLElement => !!e)

      for (const child of children) {
        if (!child.id) {
          child.setAttribute('id', `scrollbox-item-${getRandomNumber()}`)
        }
      }

      this.$emit('initialized', {
        scrollbox: this,
        wrapper: this.wrapper,
        children,
      })
    },
  },
})

export default CarouselScrollBox
</script>

<template>
  <div class="scrollbox overflow-hidden">
    <ul
      ref="wrapper"
      class="wrap scrollbar-none -m-1 flex snap-x snap-mandatory flex-row overflow-x-auto sm:-m-1.5"
    >
      <slot></slot>
    </ul>
  </div>
</template>
